import React, { useMemo, useState } from 'react';
import { Select, Modal, Button, Row, Col, Card, Tooltip, Space } from 'antd';
import IconThumb from './IconThumb';
import { listIconStyles } from '../core';
import { useBuiltInIcons } from '../hooks/useBuiltInIcons';
import { useUploadedIcons } from '../hooks/useUploadedIcons';

/**
 * 通用图标选择器（模块化，供任意页面调用）
 *
 * @param {object} props
 * @param {string|number|null} props.value 内置 key 或上传图标 id（由 valueMode 决定）
 * @param {(v: any) => void} props.onChange
 * @param {'builtin'|'uploaded'|'both'} [props.source='builtin']
 * @param {'key'|'id'} [props.valueMode='key'] uploaded 时用 id
 * @param {boolean} [props.allowClear]
 * @param {boolean} [props.showPreview]
 * @param {string} [props.placeholder]
 * @param {object} [props.style]
 * @param {string} [props.size]
 * @param {function} [props.apiFetch] 自定义 fetch（上传库）；默认内部 apiFetch
 */
export default function IconPicker({
  value,
  onChange,
  source = 'builtin',
  valueMode = 'key',
  allowClear = false,
  showPreview = true,
  placeholder = '选择图标',
  style = {},
  size = 'default',
  apiFetch
}) {
  const builtIn = useBuiltInIcons({ enabled: source === 'builtin' || source === 'both' });
  const uploaded = useUploadedIcons({
    enabled: source === 'uploaded' || source === 'both',
    apiFetch
  });
  const [gridOpen, setGridOpen] = useState(false);
  const [styleFilter, setStyleFilter] = useState('chrome');
  const styleOptions = useMemo(() => [
    { label: '全部风格', value: 'all' },
    ...listIconStyles().map((s) => ({ label: s.label, value: s.id }))
  ], []);

  const options = useMemo(() => {
    const rows = [];
    if (source === 'builtin' || source === 'both') {
      (builtIn.icons || []).forEach((ic) => {
        if (styleFilter !== 'all' && ic.style && ic.style !== styleFilter) return;
        rows.push({
          value: ic.value,
          label: ic.label,
          description: ic.description,
          url: ic.url,
          group: ic.styleLabel || '内置水滴'
        });
      });
    }
    if (source === 'uploaded' || source === 'both') {
      (uploaded.icons || []).forEach((ic) => {
        const v = valueMode === 'id' ? ic.id : (ic.file_path || ic.url);
        rows.push({
          value: v,
          label: ic.name || `图标 #${ic.id}`,
          description: ic.description || '上传图标',
          url: ic.url || ic.file_path,
          iconId: ic.id,
          group: '上传库',
          raw: ic
        });
      });
    }
    return rows;
  }, [source, valueMode, builtIn.icons, uploaded.icons, styleFilter]);

  const current = options.find((o) => o.value === value || String(o.value) === String(value));
  const loading = builtIn.loading || uploaded.loading;
  const showStyleFilter = source === 'builtin' || source === 'both';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
      {showStyleFilter && (
        <Select
          size="small"
          value={styleFilter}
          options={styleOptions}
          onChange={setStyleFilter}
          style={{ minWidth: 120 }}
        />
      )}
      <Select
        value={value == null || value === '' ? undefined : value}
        onChange={(v) => onChange?.(v)}
        allowClear={allowClear}
        placeholder={placeholder}
        style={{ minWidth: 200, ...style }}
        size={size}
        loading={loading}
        showSearch
        optionFilterProp="label"
        options={options.map((o) => ({
          value: o.value,
          label: o.label,
          url: o.url,
          iconId: o.iconId,
          group: o.group,
          description: o.description
        }))}
        optionRender={(opt) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconThumb url={opt.data.url} iconKey={opt.data.value} iconId={opt.data.iconId} size={20} />
            <span>{opt.data.label}</span>
            {opt.data.group ? (
              <span style={{ color: '#999', fontSize: 12 }}>({opt.data.group})</span>
            ) : null}
          </div>
        )}
        labelRender={(props) => {
          const o = options.find((x) => x.value === props.value || String(x.value) === String(props.value));
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {o ? <IconThumb url={o.url} iconKey={String(o.value)} iconId={o.iconId} size={18} /> : null}
              {o?.label || props.value}
            </span>
          );
        }}
      />
      {showPreview && current && (
        <Space size={4} style={{ marginLeft: 4, fontSize: 12, color: '#666' }}>
          <IconThumb url={current.url} iconKey={String(current.value)} iconId={current.iconId} size={18} />
          {current.label}
        </Space>
      )}
      {showPreview && (
        <Tooltip title="预览全部">
          <Button type="link" size="small" onClick={() => setGridOpen(true)}>预览</Button>
        </Tooltip>
      )}
      <Modal
        title="选择图标"
        open={gridOpen}
        onCancel={() => setGridOpen(false)}
        footer={null}
        width={840}
      >
        {showStyleFilter && (
          <div style={{ marginBottom: 12 }}>
            <Select
              value={styleFilter}
              options={styleOptions}
              onChange={setStyleFilter}
              style={{ minWidth: 160 }}
            />
          </div>
        )}
        <Row gutter={[12, 12]}>
          {options.map((o) => (
            <Col key={String(o.value)} xs={12} sm={8} md={6} lg={4}>
              <Card
                hoverable
                size="small"
                style={{
                  textAlign: 'center',
                  border: String(value) === String(o.value) ? '2px solid #1890ff' : '1px solid #d9d9d9'
                }}
                onClick={() => {
                  onChange?.(o.value);
                  setGridOpen(false);
                }}
              >
                <div style={{ marginBottom: 6 }}>
                  <IconThumb url={o.url} iconKey={String(o.value)} iconId={o.iconId} size={36} />
                </div>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>{o.label}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{o.group}</div>
              </Card>
            </Col>
          ))}
        </Row>
      </Modal>
    </div>
  );
}
